export interface LocationData {
  slug: string;
  state: string;
  /** If set, this is a city page under a parent state */
  parentState?: string;
  metaTitle: string;
  metaDesc: string;
  heroHeadline: string;
  heroSub: string;
  intro: string;
  cities: string[];
  industries: string[];
  whyUs: string[];
  /** Top product keywords people search for in this area */
  topProducts?: string[];
}

/* ──────── State-level pages ──────── */

const tamilNadu: LocationData = {
  slug: "tamil-nadu",
  state: "Tamil Nadu",
  metaTitle: "MS & GI Pipes Supplier in Tamil Nadu | Taher Steel Tube Co.",
  metaDesc: "Leading MS & GI pipes, ERW, seamless tubes, valves & fire safety supplier in Tamil Nadu. Serving Coimbatore, Chennai, Madurai, Salem & more since 1990.",
  heroHeadline: "Your Trusted Steel Partner in Tamil Nadu",
  heroSub: "Headquartered in Coimbatore, supplying MS & GI pipes, steel tubes, valves & fire safety systems across Tamil Nadu since 1990.",
  intro: "With over 35 years of operations from our Coimbatore headquarters, Taher Steel Tube Co. is one of Tamil Nadu's most trusted suppliers of MS pipes, GI pipes, ERW tubes, seamless pipes, valves, fittings, and fire safety equipment. We serve contractors, fabricators, government projects, and industrial buyers with ISI-certified products and competitive pricing.",
  cities: ["Coimbatore", "Chennai", "Madurai", "Salem", "Tirupur", "Erode", "Trichy", "Tirunelveli", "Vellore", "Hosur"],
  industries: ["Textile mills & spinning units", "Automobile & auto-component manufacturing", "Construction & real estate", "Water supply & plumbing infrastructure", "Sugar & chemical plants", "Government & semi-government projects"],
  whyUs: [
    "Headquartered in Coimbatore with 35+ years of local presence",
    "Same-day dispatch for stock items across Tamil Nadu",
    "Authorized Jindal, Tata, APL Apollo, Zoloto & Kartar dealer",
    "Dedicated support for government tenders & bulk projects",
  ],
  topProducts: ["MS Round Pipes", "GI Pipes", "ERW Tubes", "Seamless Pipes", "Zoloto Valves", "Fire Safety Systems"],
};

const kerala: LocationData = {
  slug: "kerala",
  state: "Kerala",
  metaTitle: "MS & GI Pipes Supplier in Kerala | Taher Steel Tube Co.",
  metaDesc: "Reliable MS & GI pipes, ERW tubes, seamless pipes, valves & fire safety supplier for Kerala. Delivering to Kochi, Thrissur, Kozhikode & all districts.",
  heroHeadline: "Reliable Steel Supply for Kerala",
  heroSub: "Delivering premium MS & GI pipes, steel tubes, valves & fire safety equipment across Kerala from our Coimbatore base.",
  intro: "Taher Steel Tube Co. has been a preferred steel supplier for Kerala's construction and industrial sectors for over three decades. Our proximity to the Kerala border from Coimbatore ensures fast delivery to all major cities and project sites. We supply ISI-certified MS pipes, GI pipes, ERW tubes, seamless pipes, valves, fittings, and complete fire safety systems.",
  cities: ["Kochi", "Thrissur", "Kozhikode", "Thiruvananthapuram", "Palakkad", "Kannur", "Malappuram", "Kollam", "Alappuzha", "Kottayam"],
  industries: ["Construction & high-rise buildings", "Shipbuilding & marine infrastructure", "Tourism & hospitality projects", "Water supply & irrigation", "Rubber & spice processing plants", "Government infrastructure projects"],
  whyUs: [
    "Fast delivery from Coimbatore — close proximity to Kerala border",
    "Trusted supplier to Kerala's construction & marine sectors",
    "Complete fire safety systems for high-rise compliance",
    "Competitive pricing with flexible bulk order options",
  ],
  topProducts: ["GI Pipes", "MS Round Pipes", "Fire Safety Systems", "Pipe Fittings", "Zoloto Valves"],
};

const karnataka: LocationData = {
  slug: "karnataka",
  state: "Karnataka",
  metaTitle: "MS & GI Pipes Supplier in Karnataka | Taher Steel Tube Co.",
  metaDesc: "Premium MS & GI pipes, ERW tubes, seamless pipes, valves & fire safety supplier in Karnataka. Serving Bangalore, Mysore, Hubli & pan-Karnataka.",
  heroHeadline: "Premium Steel Solutions for Karnataka",
  heroSub: "Supplying MS & GI pipes, ERW & seamless tubes, valves & fire safety systems to Karnataka's booming construction & industrial sectors.",
  intro: "Taher Steel Tube Co. serves Karnataka's thriving construction and manufacturing industries with a comprehensive range of steel products. From Bangalore's IT corridors to Hubli's industrial zones, we deliver ISI-certified MS pipes, GI pipes, ERW tubes, seamless pipes, valves, fittings, and fire safety equipment with reliable logistics and competitive pricing.",
  cities: ["Bangalore", "Mysore", "Hubli-Dharwad", "Mangalore", "Belgaum", "Gulbarga", "Davangere", "Shimoga", "Tumkur", "Hassan"],
  industries: ["IT parks & commercial construction", "Manufacturing & heavy engineering", "Infrastructure & metro projects", "Pharmaceutical & biotech", "Food processing & beverages", "Real estate & residential townships"],
  whyUs: [
    "Efficient logistics corridor from Coimbatore to Bangalore & beyond",
    "Serving Karnataka's IT, manufacturing & infrastructure sectors",
    "Large inventory for immediate dispatch on bulk orders",
    "Authorized dealer for India's top steel brands",
  ],
  topProducts: ["MS Square Pipes", "MS Rectangular Pipes", "ERW Tubes", "Seamless Pipes", "Scaffolding Pipes"],
};

/* ──────── City-level pages ──────── */

const coimbatore: LocationData = {
  slug: "coimbatore",
  state: "Coimbatore",
  parentState: "Tamil Nadu",
  metaTitle: "MS & GI Pipes Dealer in Coimbatore | Taher Steel Tube Co.",
  metaDesc: "Best MS & GI pipes, ERW tubes, seamless pipes, valves, flanges & fire safety dealer in Coimbatore. Authorized Jindal, Tata, APL Apollo stockist. Visit our showroom.",
  heroHeadline: "Coimbatore's Leading Steel Pipes Dealer",
  heroSub: "Walk-in showroom at Kattoor. Largest stock of MS & GI pipes, ERW tubes, valves & fire safety equipment in Coimbatore. Same-day pickup or delivery.",
  intro: "Located in the heart of Coimbatore at No. 74 Ranga Konar St, Kattoor, Taher Steel Tube Co. has served the city's industrial and construction needs for over 35 years. As an authorized dealer for Jindal, Tata, and APL Apollo, we maintain one of the largest inventories of MS pipes, GI pipes, ERW tubes, seamless pipes, valves, and fire safety equipment in the Coimbatore region. Walk-in customers get instant pricing and same-day pickup.",
  cities: ["Kattoor", "RS Puram", "Gandhipuram", "Singanallur", "Saibaba Colony", "Peelamedu", "Sulur", "Mettupalayam", "Pollachi", "Annur"],
  industries: ["Textile mills & spinning units", "Automobile manufacturing", "Pump & motor industry", "Foundries & fabrication units", "Construction & real estate", "Water supply infrastructure"],
  whyUs: [
    "Walk-in showroom with on-the-spot pricing",
    "Largest pipe & tube inventory in Coimbatore",
    "Same-day pickup or delivery within Coimbatore city",
    "35+ years of trusted local presence since 1990",
  ],
  topProducts: ["MS Round Pipes", "GI Pipes", "ERW Tubes", "Zoloto Valves", "Pipe Fittings", "Scaffolding Pipes"],
};

const chennai: LocationData = {
  slug: "chennai",
  state: "Chennai",
  parentState: "Tamil Nadu",
  metaTitle: "MS & GI Pipes Supplier in Chennai | Taher Steel Tube Co.",
  metaDesc: "Bulk MS & GI pipes, ERW tubes, seamless pipes, valves & fire safety supplier for Chennai. Jindal, Tata, APL Apollo authorized dealer. Fast delivery.",
  heroHeadline: "Bulk Steel Pipes Supplier for Chennai",
  heroSub: "Fast delivery of MS & GI pipes, ERW tubes, seamless pipes, valves & fire safety systems to Chennai from our Coimbatore warehouse.",
  intro: "Chennai's booming construction and infrastructure sector demands reliable steel supply. Taher Steel Tube Co. provides fast, bulk delivery of ISI-certified MS pipes, GI pipes, ERW tubes, seamless pipes, stainless steel pipes, valves, fittings, and fire safety equipment to contractors, builders, and industrial buyers across Chennai and surrounding areas.",
  cities: ["Ambattur", "Guindy", "Manali", "Ennore", "Thiruvallur", "Sriperumbudur", "Mahabalipuram", "Tambaram", "Avadi", "Porur"],
  industries: ["Automobile & auto ancillaries", "Port & logistics infrastructure", "IT & commercial construction", "Real estate & residential", "Petrochemicals & refineries", "Government & metro rail projects"],
  whyUs: [
    "Dedicated Chennai delivery route — 2-3 day turnaround",
    "Bulk order specialists for construction & infrastructure",
    "Competitive pricing for metro & government projects",
    "Full range from pipes to fire safety in a single order",
  ],
  topProducts: ["MS Round Pipes", "MS Square Pipes", "GI Pipes", "Seamless Pipes", "Fire Hydrant Systems", "Flanges"],
};

const madurai: LocationData = {
  slug: "madurai",
  state: "Madurai",
  parentState: "Tamil Nadu",
  metaTitle: "MS & GI Pipes Supplier in Madurai | Taher Steel Tube Co.",
  metaDesc: "Reliable MS & GI pipes, ERW tubes, valves & fire safety supplier in Madurai. Authorized Jindal, Tata, APL Apollo dealer. Quick dispatch from Coimbatore.",
  heroHeadline: "Trusted Steel Supplier for Madurai",
  heroSub: "Quick dispatch of MS & GI pipes, steel tubes, valves & fire safety equipment to Madurai and southern Tamil Nadu.",
  intro: "Taher Steel Tube Co. serves Madurai's growing construction and industrial sectors with prompt dispatch from our Coimbatore warehouse. We supply ISI-certified MS pipes, GI pipes, ERW tubes, valves, fittings, and fire safety equipment at competitive prices for contractors, fabricators, and project buyers across Madurai and surrounding districts.",
  cities: ["Madurai City", "Dindigul", "Theni", "Sivaganga", "Virudhunagar", "Ramanathapuram"],
  industries: ["Textile & handloom industry", "Construction & residential projects", "Temple & heritage restoration", "Food processing & cold storage", "Water supply & irrigation", "Government infrastructure"],
  whyUs: [
    "Quick dispatch — Madurai is just 4 hours from our warehouse",
    "Competitive pricing for bulk construction orders",
    "Trusted supplier for temple & government projects",
    "Full product range available in a single order",
  ],
  topProducts: ["MS Round Pipes", "GI Pipes", "ERW Tubes", "Pipe Fittings", "Zoloto Valves"],
};

const bangalore: LocationData = {
  slug: "bangalore",
  state: "Bangalore",
  parentState: "Karnataka",
  metaTitle: "MS & GI Pipes Supplier in Bangalore | Taher Steel Tube Co.",
  metaDesc: "Premium MS & GI pipes, ERW tubes, seamless pipes, valves & fire safety supplier in Bangalore. Jindal, Tata, APL Apollo authorized dealer. Bulk supply.",
  heroHeadline: "Premium Steel Pipes for Bangalore",
  heroSub: "Supplying MS & GI pipes, ERW tubes, seamless pipes, valves & fire safety systems for Bangalore's IT parks, metros & construction boom.",
  intro: "Bangalore's rapid urbanization demands reliable steel supply partners. Taher Steel Tube Co. supplies ISI-certified MS pipes, GI pipes, ERW tubes, seamless pipes, stainless steel pipes, valves, fittings, and fire safety equipment to Bangalore's construction, IT infrastructure, and manufacturing sectors. Our efficient Coimbatore-Bangalore logistics corridor ensures fast delivery.",
  cities: ["Whitefield", "Electronic City", "Peenya", "Yelahanka", "Hosur Road", "Tumkur Road", "Devanahalli", "Nelamangala", "Bidadi", "Anekal"],
  industries: ["IT parks & tech campuses", "Metro rail & infrastructure", "Commercial & residential construction", "Manufacturing & engineering", "Warehouse & logistics parks", "Pharmaceutical & biotech"],
  whyUs: [
    "Fast Coimbatore → Bangalore delivery corridor",
    "Specialists in IT park & commercial construction supply",
    "Fire safety systems for high-rise compliance (NBC norms)",
    "Bulk pricing for metro & infrastructure projects",
  ],
  topProducts: ["MS Square Pipes", "MS Rectangular Pipes", "Seamless Pipes", "Fire Safety Systems", "Scaffolding Pipes", "Stainless Steel Pipes"],
};

const kochi: LocationData = {
  slug: "kochi",
  state: "Kochi",
  parentState: "Kerala",
  metaTitle: "MS & GI Pipes Supplier in Kochi | Taher Steel Tube Co.",
  metaDesc: "Leading MS & GI pipes, ERW tubes, seamless pipes, valves & fire safety supplier in Kochi, Kerala. Authorized dealer. Fast delivery from Coimbatore.",
  heroHeadline: "Reliable Steel Supply for Kochi",
  heroSub: "Fast delivery of MS & GI pipes, steel tubes, valves & fire safety equipment to Kochi and central Kerala.",
  intro: "Kochi's thriving construction, marine, and infrastructure sectors need a dependable steel supplier. Taher Steel Tube Co. delivers ISI-certified MS pipes, GI pipes, ERW tubes, seamless pipes, valves, fittings, and fire safety equipment to Kochi and surrounding areas with fast turnaround thanks to our Coimbatore proximity via the Palakkad route.",
  cities: ["Ernakulam", "Aluva", "Kakkanad", "Edappally", "Kalamassery", "Tripunithura", "Angamaly", "Perumbavoor", "Muvattupuzha", "North Paravur"],
  industries: ["Marine & shipbuilding", "Metro rail & infrastructure", "Commercial construction & malls", "Tourism & hospitality", "Petrochemicals & refinery", "IT parks & SEZs"],
  whyUs: [
    "Quick delivery via Palakkad route — close to Kerala border",
    "Specialists in marine-grade & corrosion-resistant pipes",
    "Fire safety systems for high-rise buildings (Kerala compliance)",
    "Trusted by Kochi's top construction contractors",
  ],
  topProducts: ["GI Pipes", "Stainless Steel Pipes", "Fire Safety Systems", "Pipe Fittings", "Zoloto Valves", "Seamless Pipes"],
};

const salem: LocationData = {
  slug: "salem",
  state: "Salem",
  parentState: "Tamil Nadu",
  metaTitle: "MS & GI Pipes Supplier in Salem | Taher Steel Tube Co.",
  metaDesc: "Trusted MS & GI pipes, ERW tubes, valves & fire safety supplier in Salem. Authorized Jindal, Tata, APL Apollo dealer. Same-day dispatch from Coimbatore.",
  heroHeadline: "Steel Pipes Supplier for Salem",
  heroSub: "Same-day dispatch of MS & GI pipes, ERW tubes, valves & fire safety equipment to Salem and western Tamil Nadu.",
  intro: "Salem's steel and industrial hub demands quality pipe and tube supply. Taher Steel Tube Co. provides ISI-certified MS pipes, GI pipes, ERW tubes, seamless pipes, valves, fittings, and fire safety equipment to Salem's foundries, construction firms, and infrastructure projects with same-day dispatch from our Coimbatore warehouse.",
  cities: ["Salem City", "Attur", "Mettur", "Namakkal", "Rasipuram", "Tiruchengode"],
  industries: ["Steel foundries & smelters", "Construction & real estate", "Automobile component units", "Textile & dyeing units", "Government infrastructure", "Water supply projects"],
  whyUs: [
    "Same-day dispatch — Salem is just 3 hours from Coimbatore",
    "Serving Salem's steel industry for over 30 years",
    "Authorized dealer for top brands at competitive rates",
    "Bulk supply for foundries & fabrication workshops",
  ],
  topProducts: ["MS Round Pipes", "ERW Tubes", "GI Pipes", "Pipe Fittings", "Flanges"],
};

/* ──────── New city pages ──────── */

const tirupur: LocationData = {
  slug: "tirupur",
  state: "Tirupur",
  parentState: "Tamil Nadu",
  metaTitle: "MS & GI Pipes Supplier in Tirupur | Taher Steel Tube Co.",
  metaDesc: "Buy MS & GI pipes, ERW tubes, seamless pipes, valves & fire safety equipment in Tirupur. Authorized Jindal, Tata, APL Apollo dealer. Same-day dispatch from Coimbatore.",
  heroHeadline: "Steel Pipes Supplier for Tirupur",
  heroSub: "Same-day dispatch of MS & GI pipes, ERW tubes, valves & fire safety systems to Tirupur from our Coimbatore warehouse — just 50 km away.",
  intro: "Tirupur's world-famous textile and garment industry relies on robust infrastructure — from water supply pipelines to factory construction and fire safety compliance. Taher Steel Tube Co. is the preferred MS & GI pipes supplier for Tirupur, delivering ISI-certified pipes, tubes, valves, fittings, and fire safety equipment with same-day dispatch from our Coimbatore headquarters.",
  cities: ["Tirupur City", "Avinashi", "Palladam", "Udumalpet", "Dharapuram", "Kangeyam"],
  industries: ["Textile & garment manufacturing", "Dyeing & bleaching units", "Water supply & ETP piping", "Construction & real estate", "Pump & motor manufacturing", "Fire safety compliance"],
  whyUs: [
    "Same-day dispatch — Tirupur is just 50 km from Coimbatore",
    "Specialists in textile mill piping & fire safety",
    "Authorized dealer for Jindal, Tata & APL Apollo",
    "Competitive pricing for bulk orders",
  ],
  topProducts: ["GI Pipes", "MS Round Pipes", "ERW Tubes", "Fire Safety Systems", "Pipe Fittings", "Zoloto Valves"],
};

const trichy: LocationData = {
  slug: "trichy",
  state: "Trichy",
  parentState: "Tamil Nadu",
  metaTitle: "MS & GI Pipes Supplier in Trichy | Taher Steel Tube Co.",
  metaDesc: "Reliable MS & GI pipes, ERW tubes, seamless pipes, valves & fire safety supplier in Trichy. Authorized dealer. Fast delivery from Coimbatore.",
  heroHeadline: "Reliable Steel Supply for Trichy",
  heroSub: "Fast delivery of MS & GI pipes, steel tubes, valves & fire safety equipment to Tiruchirappalli and central Tamil Nadu.",
  intro: "Tiruchirappalli (Trichy) is a major industrial hub in central Tamil Nadu with BHEL, ordnance factories, and booming construction. Taher Steel Tube Co. supplies ISI-certified MS pipes, GI pipes, ERW tubes, seamless pipes, valves, fittings, and fire safety equipment to Trichy's industrial and construction sectors with fast dispatch from Coimbatore.",
  cities: ["Trichy City", "Srirangam", "Lalgudi", "Musiri", "Manapparai", "Thottiyam"],
  industries: ["Heavy engineering (BHEL)", "Ordnance & defence manufacturing", "Construction & infrastructure", "Temple & heritage projects", "Food processing & cold storage", "Government infrastructure"],
  whyUs: [
    "Fast dispatch — Trichy is ~4 hours from Coimbatore",
    "Serving BHEL contractors & heavy engineering firms",
    "Full product range from pipes to fire safety",
    "Competitive bulk pricing for infrastructure projects",
  ],
  topProducts: ["MS Round Pipes", "Seamless Pipes", "ERW Tubes", "GI Pipes", "Flanges", "Fire Safety Systems"],
};

const erode: LocationData = {
  slug: "erode",
  state: "Erode",
  parentState: "Tamil Nadu",
  metaTitle: "MS & GI Pipes Supplier in Erode | Taher Steel Tube Co.",
  metaDesc: "Trusted MS & GI pipes, ERW tubes, valves & fire safety supplier in Erode. Authorized Jindal, Tata, APL Apollo dealer. Quick dispatch from Coimbatore.",
  heroHeadline: "Steel Pipes Dealer for Erode",
  heroSub: "Quick dispatch of MS & GI pipes, ERW tubes, valves & fire safety equipment to Erode and western Tamil Nadu.",
  intro: "Erode's thriving textile, turmeric, and construction industries require a reliable steel supply partner. Taher Steel Tube Co. delivers ISI-certified MS pipes, GI pipes, ERW tubes, valves, pipe fittings, and fire safety equipment to Erode with quick turnaround from our Coimbatore warehouse — just 100 km away.",
  cities: ["Erode City", "Bhavani", "Gobichettipalayam", "Sathyamangalam", "Perundurai", "Anthiyur"],
  industries: ["Textile & handloom industry", "Turmeric & agricultural processing", "Construction & real estate", "Water supply & irrigation", "Sugar mills", "Government projects"],
  whyUs: [
    "Quick dispatch — Erode is just ~100 km from Coimbatore",
    "Trusted supplier for textile mill piping",
    "Authorized dealer for India's top brands",
    "Bulk pricing for construction & infrastructure",
  ],
  topProducts: ["MS Round Pipes", "GI Pipes", "ERW Tubes", "Pipe Fittings", "Zoloto Valves"],
};

const mysore: LocationData = {
  slug: "mysore",
  state: "Mysore",
  parentState: "Karnataka",
  metaTitle: "MS & GI Pipes Supplier in Mysore | Taher Steel Tube Co.",
  metaDesc: "Premium MS & GI pipes, ERW tubes, seamless pipes, valves & fire safety supplier in Mysore. Jindal, Tata, APL Apollo authorized dealer.",
  heroHeadline: "Steel Pipes Supplier for Mysore",
  heroSub: "Supplying MS & GI pipes, ERW & seamless tubes, valves & fire safety systems to Mysore's growing construction and industrial sectors.",
  intro: "Mysore (Mysuru) is one of Karnataka's fastest-growing cities with a booming IT sector, heritage tourism, and expanding infrastructure. Taher Steel Tube Co. supplies ISI-certified MS pipes, GI pipes, ERW tubes, seamless pipes, valves, fittings, and fire safety equipment to Mysore with efficient logistics from Coimbatore via the Bangalore corridor.",
  cities: ["Mysore City", "Nanjangud", "Hunsur", "T. Narasipura", "K.R. Nagar", "Chamundi Hill"],
  industries: ["IT parks & tech centres", "Tourism & hospitality", "Food processing & beverages", "Construction & real estate", "Pharmaceutical manufacturing", "Government & heritage projects"],
  whyUs: [
    "Efficient logistics via the Coimbatore–Bangalore–Mysore corridor",
    "Serving Mysore's IT, tourism & construction sectors",
    "Fire safety systems for commercial building compliance",
    "Authorized dealer for top Indian steel brands",
  ],
  topProducts: ["MS Square Pipes", "GI Pipes", "ERW Tubes", "Fire Safety Systems", "Pipe Fittings", "Seamless Pipes"],
};

const thrissur: LocationData = {
  slug: "thrissur",
  state: "Thrissur",
  parentState: "Kerala",
  metaTitle: "MS & GI Pipes Supplier in Thrissur | Taher Steel Tube Co.",
  metaDesc: "Reliable MS & GI pipes, ERW tubes, seamless pipes, valves & fire safety supplier in Thrissur, Kerala. Fast delivery from Coimbatore via Palakkad.",
  heroHeadline: "Trusted Steel Supply for Thrissur",
  heroSub: "Fast delivery of MS & GI pipes, steel tubes, valves & fire safety equipment to Thrissur and central Kerala via Palakkad.",
  intro: "Thrissur, the cultural capital of Kerala, has a rapidly growing construction and commercial sector. Taher Steel Tube Co. delivers ISI-certified MS pipes, GI pipes, ERW tubes, seamless pipes, valves, fittings, and fire safety equipment to Thrissur with fast turnaround via the Palakkad route from our Coimbatore headquarters.",
  cities: ["Thrissur City", "Guruvayur", "Chalakudy", "Irinjalakuda", "Kodungallur", "Kunnamkulam"],
  industries: ["Construction & real estate", "Gold & jewellery manufacturing", "Tourism & temple projects", "Commercial & retail complexes", "Water supply infrastructure", "Fire safety compliance"],
  whyUs: [
    "Fast delivery via Palakkad — close to Kerala border",
    "Trusted by Thrissur's construction & commercial sector",
    "Complete fire safety systems for building compliance",
    "Competitive pricing with bulk order flexibility",
  ],
  topProducts: ["GI Pipes", "MS Round Pipes", "Fire Safety Systems", "Pipe Fittings", "Zoloto Valves", "Flanges"],
};

const hubli: LocationData = {
  slug: "hubli",
  state: "Hubli",
  parentState: "Karnataka",
  metaTitle: "MS & GI Pipes Supplier in Hubli | Taher Steel Tube Co.",
  metaDesc: "Premium MS & GI pipes, ERW tubes, seamless pipes, valves & fire safety supplier in Hubli-Dharwad. Authorized Jindal, Tata, APL Apollo dealer.",
  heroHeadline: "Steel Pipes Supplier for Hubli-Dharwad",
  heroSub: "Supplying MS & GI pipes, ERW tubes, seamless pipes, valves & fire safety systems to Hubli-Dharwad's industrial and construction sectors.",
  intro: "Hubli-Dharwad is North Karnataka's largest commercial and industrial centre. Taher Steel Tube Co. supplies ISI-certified MS pipes, GI pipes, ERW tubes, seamless pipes, valves, fittings, and fire safety equipment to Hubli's engineering workshops, construction firms, and infrastructure projects with reliable logistics.",
  cities: ["Hubli City", "Dharwad", "Belgaum", "Gadag", "Haveri", "Ranebennur"],
  industries: ["Engineering & machine tool manufacturing", "Construction & infrastructure", "Agricultural processing", "Textile industry", "Railway workshops", "Government projects"],
  whyUs: [
    "Reliable supply to North Karnataka's industrial hub",
    "Specialists in engineering & construction steel supply",
    "Full product range from pipes to fire safety in one order",
    "Authorized dealer for India's top steel brands",
  ],
  topProducts: ["MS Round Pipes", "MS Square Pipes", "ERW Tubes", "Pipe Fittings", "Seamless Pipes", "Flanges"],
};

/* ──────── Export ──────── */

export const locations: LocationData[] = [
  tamilNadu,
  kerala,
  karnataka,
  coimbatore,
  chennai,
  madurai,
  bangalore,
  kochi,
  salem,
  tirupur,
  trichy,
  erode,
  mysore,
  thrissur,
  hubli,
];

export const getLocationBySlug = (slug: string) =>
  locations.find((l) => l.slug === slug);

/** Get all city pages under a state */
export const getCitiesByState = (stateSlug: string) =>
  locations.filter((l) => l.parentState && l.slug !== stateSlug &&
    locations.find(s => s.slug === stateSlug)?.state === l.parentState);
